import axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import { throttle } from '../../../../utilities/helperFunctions';
import { EmitMetric } from '../../../Analytics/VisibilityPixel/VisibilityPixel';
import ErrorLabel from '../../../Elements/ErrorLabel/ErrorLabel';
import Tag from '../../../Elements/Tag/Tag';
import { groupSearchSchema } from '../../../Includes/FormSchema/ContentSchema';
import { ValidateForm } from '../../../Includes/FormValidation';
import Button from '../../../Input/Button/Button';
import FormElementWithLabel from '../../../Input/FormElementWithLabel/FormElementWithLabel';
import InputField from '../../../Input/InputField/InputField';
import LocationInput from '../../../Input/LocationInput/LocationInput';

import { buildImagePath } from '../../../JobComponents/Company/Image/logo';
import InterestTypeahead from '../../InterestTypeahead/InterestTypeahead';
import GroupSearchResults from './GroupSearchResults/GroupSearchResults';

import './SearchPageSection.scss';

export default class SearchPageSection extends React.PureComponent {
    constructor(props) {
        super();
        this.state = {
            keyword: props.keyword,
            interests: props.interests,
            locations: props.locations,
            results: props.searchMatches ? props.searchMatches.results : [],
            startNum: props.searchMatches ? props.searchMatches.startNum : 0,
            endNum: props.searchMatches ? props.searchMatches.endNum : 0,
            total: props.searchMatches ? props.searchMatches.total : 0,
            resultsPerPage: props.searchMatches
                ? props.searchMatches.resultsPerPage
                : 6,
            page: props.page || 1,
            isViewWidthChecked: false,
            showAllResults: true,
            isSearchLoading: false,
            isSearchActive: false,
            errorMessage: '',
        };

        this.keywordSearchTimeout = null;

        this.throttleInterval = 150;
        this.throttledCheckWindowViewWidth = throttle(
            this.checkWindowViewWidth,
            this.throttleInterval
        );
    }

    static propTypes = {
        keyword: PropTypes.string,
        interests: PropTypes.arrayOf(
            PropTypes.shape({
                /**
                 * interst name
                 */
                name: PropTypes.string,
                /**
                 * interest id
                 */
                id: PropTypes.number,
            })
        ),
        locations: PropTypes.arrayOf(
            PropTypes.shape({
                /**
                 * location name
                 */
                name: PropTypes.string,
                /**
                 * location latitude
                 */
                latitude: PropTypes.string,
                /**
                 * location longitude
                 */
                longitude: PropTypes.string,
            })
        ),
        searchMatches: PropTypes.object,
    };

    static defaultProps = {
        keyword: '',
        interests: [],
        locations: [],
        page: 1,
        searchResults: null,
    };

    componentDidMount() {
        this.throttledCheckWindowViewWidth();
        window.addEventListener('resize', this.throttledCheckWindowViewWidth);
        this.searchGroups();
    }

    componentWillUnmount() {
        window.removeEventListener(
            'resize',
            this.throttledCheckWindowViewWidth
        );
    }

    checkWindowViewWidth = () => {
        const html = document.documentElement;
        const windowWidth = window.innerWidth || html.clientWidth;
        const { results, endNum, resultsPerPage } = this.state;

        if (windowWidth < 768) {
            this.setState({
                showAllResults: true,
                isViewWidthChecked: true,
            });
        } else {
            const isResultsNeedToBePaginated =
                results && results.length > resultsPerPage;
            this.setState({
                showAllResults: false,
                isViewWidthChecked: true,
            });

            if (isResultsNeedToBePaginated) {
                const isLastPageResultsLessThanResultsPerPage =
                    endNum % resultsPerPage !== 0;
                const derivedStartNum = isLastPageResultsLessThanResultsPerPage
                    ? endNum - (endNum % resultsPerPage) + 1
                    : endNum - resultsPerPage + 1;

                this.setState({
                    startNum: derivedStartNum,
                    results: results.slice(
                        results.length - (endNum - derivedStartNum + 1)
                    ),
                });
            }
        }
    };

    clearInputs = () => {
        this.setState(
            {
                keyword: '',
                interests: [],
                locations: [],
                results: [],
                page: 1,
                errorMessage: '',
                isSearchActive: false,
            },
            this.buildWindowQueryStr
        );
    };

    handleClearClick = () => {
        this.clearInputs();
        return EmitMetric({
            misc_event_type: 'group-search-clear-click',
            misc_event_count: 1,
        });
    };

    handleInputChange = (key) => (value) => {
        this.setState({ [key]: value, results: null }, this.searchGroups);
    };

    handleKeywordChange = (value) => {
        this.setState({ keyword: value, results: null });
        clearTimeout(this.keywordSearchTimeout);
        this.keywordSearchTimeout = setTimeout(this.searchGroups, 500);
    };

    handleTagRemove = (item) => () => {
        const currentItems = this.state[item.type];
        const updatedItems = currentItems.filter(
            ({ value, name }) => name !== item.name
        );
        this.handleInputChange(item.type)(updatedItems);
    };

    serializeInterests = (interests) => {
        const serializedInterests = interests.map((interest) => ({
            id: interest.id,
            name:
                interest.parent && interest.parent.name
                    ? interest.parent.name
                    : interest.name,
            parent: interest.parent || null,
        }));

        this.handleInputChange('interests')(serializedInterests);
    };

    serializeLocations = (locations) => {
        const serializedLocations = locations.map((location) => ({
            latitude: location.coordinates
                ? location.coordinates.lat
                : location.latitude,
            longitude: location.coordinates
                ? location.coordinates.lng
                : location.longitude,
            name: location.name || location.value,
        }));

        this.handleInputChange('locations')(serializedLocations);
    };

    buildWindowQueryStr = () => {
        if (window) {
            let queryStr = this.state.keyword
                ? `keyword=${encodeURIComponent(this.state.keyword)}`
                : '';

            queryStr += this.state.page > 1 ? `&page=${this.state.page}` : '';

            if (this.state.interests.length > 0) {
                queryStr += `&interests=${JSON.stringify(
                    this.state.interests
                )}`;
            }

            if (this.state.locations.length > 0) {
                queryStr += `&locations=${JSON.stringify(
                    this.state.locations
                )}`;
            }

            if (queryStr) {
                queryStr = `?${queryStr}`;
            }

            window.history.replaceState({}, '', `/groups/search${queryStr}`);
        }
    };

    searchGroups = async () => {
        const isSearchActive = this.getIsSearchActive();

        if (!isSearchActive) {
            this.clearInputs();
            return;
        }

        const {
            keyword,
            interests,
            locations,
            page,
            resultsPerPage,
        } = this.state;

        const isSearchQueryValid = await ValidateForm(
            { keyword, interests, locations },
            groupSearchSchema,
            (error) => {
                this.setState({
                    errorMessage: 'Something went wrong. Please try again.',
                });
            }
        );

        if (!isSearchQueryValid) {
            return;
        }

        this.buildWindowQueryStr();

        this.setState({
            isSearchLoading: true,
            errorMessage: '',
            isSearchActive,
        });

        EmitMetric({ misc_event_type: 'group-search', misc_event_count: 1 });

        axios
            .post(`/api/groups/search`, {
                keyword,
                interests,
                locations,
                page,
                resultsPerPage,
            })
            .then((resp) => {
                const {
                    results,
                    startNum,
                    endNum,
                    total,
                    resultsPerPage,
                } = resp.data.data;

                const derivedResults = !this.state.showAllResults
                    ? results
                    : (this.state.results || []).concat(results);
                const derivedStartNum = !this.state.showAllResults
                    ? startNum
                    : endNum -
                      (this.state.results || []).concat(results).length +
                      1;

                this.setState({
                    results: derivedResults,
                    startNum: derivedStartNum,
                    endNum,
                    total,
                    isSearchLoading: false,
                    resultsPerPage: resultsPerPage || 6,
                });
            })
            .catch((e) => {
                let errorMessage = 'Something went wrong. Please try again.';

                if (e.response && e.response.data && e.response.data.errors) {
                    const errors =
                        e.response && e.response.data && e.response.data.errors;
                    const errorType = Object.keys(errors)[0];

                    if (typeof errors[errorType] === 'string') {
                        errorMessage = errors[errorType];
                    } else {
                        const errorSubType = Object.keys(
                            errors[errorType][Object.keys(errors[errorType])[0]]
                        )[0];
                        errorMessage =
                            errors[errorType][
                                Object.keys(errors[errorType])[0]
                            ][errorSubType];
                    }
                }

                this.setState({
                    isSearchLoading: false,
                    errorMessage,
                });
            });
    };

    getIsSearchActive = () => {
        return !!(
            this.state.keyword ||
            this.state.interests.length ||
            this.state.locations.length
        );
    };

    goToNextPage = () => {
        const { page, total } = this.state;
        let goToNextPage = null;
        if (page * this.state.resultsPerPage < total) {
            goToNextPage = () =>
                this.setState(
                    {
                        page: this.state.page + 1,
                    },
                    () => this.searchGroups()
                );
        }
        return goToNextPage;
    };

    goToPrevPage = () => {
        const { page } = this.state;
        let goToPrevPage = null;
        if (page > 1) {
            goToPrevPage = () =>
                this.setState(
                    {
                        page: this.state.page - 1,
                    },
                    () => this.searchGroups()
                );
        }
        return goToPrevPage;
    };

    render() {
        const tags = [
            ...this.state.interests.map((i) => ({ ...i, type: 'interests' })),
            ...this.state.locations.map((l) => ({ ...l, type: 'locations' })),
        ];

        return (
            <div className="component-Groups-Search-SearchPageSection">
                <div className="search-heading-wrapper">
                    <img
                        className="search-icon"
                        src={buildImagePath(
                            '/v1569951461/search-icon_gsistl.svg',
                            'xSmall'
                        )}
                        alt="search-icon"
                    />

                    <p className="search-heading">Discover groups</p>
                </div>

                <div className="search-inputs">
                    <div className="keyword-search">
                        <FormElementWithLabel
                            fieldLabelProps={{
                                label: 'Search by keywords:',
                                shouldShowTooltip: false,
                            }}
                        >
                            <InputField
                                inputValue={this.state.keyword}
                                name={'group-search-keyword'}
                                onChange={this.handleKeywordChange}
                                placeholder="work life balance"
                            />
                        </FormElementWithLabel>
                    </div>

                    <div className="interest-location-search">
                        <InterestTypeahead
                            fieldLabelProps={{
                                label: 'Search by interests:',
                                shouldShowTooltip: false,
                            }}
                            allowAddNewItem={false}
                            onUpdate={this.serializeInterests}
                            items={this.state.interests}
                            placeholder="engineering"
                            inputMaxLength={0}
                            withTags={false}
                        />

                        <FormElementWithLabel
                            fieldLabelProps={{
                                label: 'Search by location(s):',
                                shouldShowTooltip: false,
                            }}
                        >
                            <LocationInput
                                placeholder="New York, NY"
                                selectedLocations={this.state.locations}
                                onChange={this.serializeLocations}
                                canSelectMulti={true}
                                idValue="group-search-locations"
                                hasIcon={false}
                            />
                        </FormElementWithLabel>
                    </div>
                </div>

                <div className="search-tags">
                    {tags.map((i, idx) => (
                        <Tag
                            key={`item-${idx}`}
                            onRemove={this.handleTagRemove(i)}
                            tagCssClass={`tag-${i.type}`}
                        >
                            {i.name}
                        </Tag>
                    ))}
                </div>

                {this.state.errorMessage && (
                    <ErrorLabel error={this.state.errorMessage} />
                )}

                {this.getIsSearchActive() && (
                    <Button
                        inputStyle="clear-button"
                        onClick={this.handleClearClick}
                        type="secondary-blue-gray"
                        value="Clear"
                    />
                )}

                <GroupSearchResults
                    groupResults={this.state.results}
                    startNum={this.state.startNum}
                    endNum={this.state.endNum}
                    total={this.state.total}
                    goToPrevPage={this.goToPrevPage}
                    goToNextPage={this.goToNextPage}
                    showAllResults={this.state.showAllResults}
                    isViewWidthChecked={this.state.isViewWidthChecked}
                    isSearchActive={this.state.isSearchActive}
                    isSearchLoading={this.state.isSearchLoading}
                    resultsPerPage={this.state.resultsPerPage}
                    isSearchLoading={this.state.isSearchLoading}
                    resultsPerPage={this.resultsPerPage}
                />
            </div>
        );
    }
}
